import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

export default function Footer() {
  const { t, lang, setLanguage } = useI18n();

  const sections = [
    { id: "heritage", k: "nav.who_we_are" },
    { id: "land", k: "nav.land" },
    { id: "journey", k: "nav.seed_to_smoke" },
    { id: "craft", k: "nav.master_blender" },
    { id: "collection", k: "nav.final_cigar" },
    { id: "archive", k: "nav.archive" },
    { id: "work", k: "nav.work_with_us" },
  ];

  const goTo = (id) => {
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer"
      className="relative bg-pearl text-tobacco border-t border-tobacco/15"
    >
      {/* Single row with three tight columns */}
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-10 md:py-14 grid grid-cols-12 gap-6 md:gap-10 items-start">
        {/* LEFT — wordmark + address */}
        <div className="col-span-12 md:col-span-3">
          <Link
            to="/"
            aria-label="Elias Ramirez Cigar Makers"
            className="inline-flex flex-col"
          >
            <span className="font-display italic text-tobacco text-[26px] leading-none tracking-tight">
              Elias <span className="text-tobacco/70">Ramírez</span>
            </span>
            <span className="mt-1 font-mono text-[9px] tracking-widest2 uppercase text-gold/85">
              Cigar Makers · MCMXLVIII
            </span>
          </Link>
          <p className="mt-5 text-tobacco/65 font-light text-[12.5px] leading-relaxed max-w-[34ch]">
            {lang === "es"
              ? "Tabaco dominicano. Tiempo. Disciplina."
              : "Dominican Tobacco. Time. Discipline."}
          </p>
          <div className="mt-4 font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55 leading-relaxed">
            <div>San Juan de la Maguana · Las Matas de Farfán · Elías Piña</div>
            <div>{lang === "es" ? "República Dominicana" : "Dominican Republic"}</div>
            <div className="mt-2">
              <a
                href="mailto:office@eliasramirez.com"
                className="hover:text-tobacco transition-colors duration-500"
                data-testid="footer-email"
              >
                office@eliasramirez.com
              </a>
            </div>
          </div>
        </div>

        {/* CENTER — chapter manuscript index */}
        <div className="col-span-12 md:col-span-6">
          <div className="font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55 mb-4">
            {lang === "es" ? "Manuscrito" : "Manuscript"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-x-3 gap-y-2 max-w-3xl">
            {sections.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goTo(s.id)}
                data-testid={`footer-nav-${s.id}`}
                className="text-left group"
              >
                <span className="font-mono text-[9px] tracking-widest2 text-tobacco/55 group-hover:text-tobacco/85 transition-colors duration-500">
                  {["I", "II", "III", "IV", "V", "VI", "VII"][i]}
                </span>
                <div className="mt-0.5 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/85 group-hover:text-tobacco transition-colors duration-500">
                  {t(s.k)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT — language + journals */}
        <div className="col-span-12 md:col-span-3 md:text-right">
          <div className="font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55 mb-3">
            {lang === "es" ? "Idioma" : "Language"}
          </div>
          <div
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-widest2 uppercase"
            data-testid="footer-lang"
          >
            <button
              onClick={() => setLanguage("en")}
              data-testid="footer-lang-en"
              className={`transition-colors duration-500 ${lang === "en" ? "text-tobacco" : "text-tobacco/55 hover:text-tobacco/85"}`}
            >
              English
            </button>
            <span className="text-tobacco/35">/</span>
            <button
              onClick={() => setLanguage("es")}
              data-testid="footer-lang-es"
              className={`transition-colors duration-500 ${lang === "es" ? "text-tobacco" : "text-tobacco/55 hover:text-tobacco/85"}`}
            >
              Español
            </button>
          </div>

          <div className="mt-6 font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55 mb-3">
            {lang === "es" ? "Cuadernos" : "Journals"}
          </div>
          <div className="flex md:justify-end items-center gap-3">
            {[
              { k: "in", label: "Instagram" },
              { k: "li", label: "LinkedIn" },
              { k: "yt", label: "YouTube" },
            ].map((s) => (
              <a
                key={s.k}
                href="#"
                aria-label={s.label}
                data-testid={`footer-social-${s.k}`}
                className="w-8 h-8 border border-tobacco/30 hover:border-tobacco flex items-center justify-center font-mono text-[9px] tracking-widest2 uppercase text-tobacco/65 hover:text-tobacco transition-colors duration-500"
              >
                {s.k.toUpperCase()}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom rule — institutional copyright */}
      <div className="border-t border-tobacco/15">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="footer-copyright">
            <span>© {new Date().getFullYear()} · Elias Ramirez Cigar Makers ®</span>
            <span className="hidden md:inline w-1 h-1 bg-tobacco/40 rotate-45" />
            <span className="inline-flex items-center gap-1.5">
              <span>Powered by</span>
              <a
                href="https://eternitymp.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-eternity-link"
                className="eternity-link"
              >
                Eternity MP
              </a>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <a href="#" className="hover:text-tobacco transition-colors duration-500" data-testid="footer-privacy">
              {lang === "es" ? "Privacidad" : "Privacy"}
            </a>
            <a href="#" className="hover:text-tobacco transition-colors duration-500" data-testid="footer-terms">
              {lang === "es" ? "Términos" : "Terms"}
            </a>
            <a
              href="mailto:office@eliasramirez.com"
              className="hover:text-tobacco transition-colors duration-500"
              data-testid="footer-contact"
            >
              {lang === "es" ? "Contacto" : "Contact"}
            </a>
            <Link
              to="/login"
              className="hover:text-tobacco transition-colors duration-500"
              data-testid="footer-admin-link"
            >
              {t("footer.admin")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
